import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import heroImage from '@/assets/hero-law.jpg';

interface PasswordGateProps {
  children: React.ReactNode;
}

export const PasswordGate: React.FC<PasswordGateProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // SECURITY WARNING: This is NOT secure for production use!
  // Hardcoded passwords can be easily discovered in client-side code.
  // Consider using proper backend authentication or environment variables.
  // This should only be used for temporary construction/testing access.
  const correctPassword = import.meta.env.VITE_SITE_PASSWORD || 'ADEADE';

  useEffect(() => {
    // Check if user is already authenticated
    const isAuth = sessionStorage.getItem('site-authenticated') === 'true';
    setIsAuthenticated(isAuth);
    setIsLoading(false);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === correctPassword) {
      setIsAuthenticated(true);
      sessionStorage.setItem('site-authenticated', 'true');
      setError('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/75 to-primary/90"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="max-w-md mx-auto">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="/lovable-uploads/ecc6029e-7bf1-4bc9-b8a5-d4a887b51137.png" 
                alt="Freeman & Crown Equity Law Group" 
                className="mx-auto max-h-32 md:max-h-40 w-auto object-contain"
              />
            </div>
            
            {/* Under Construction Message */}
            <div className="mb-8">
              <h1 className="font-rockwell text-2xl md:text-3xl font-semibold mb-4">
                Site Under Construction
              </h1>
              <p className="text-white/90 text-lg">
                Please enter the access code to continue
              </p>
            </div>
            
            {/* Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter access code"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="text-center bg-white/10 border-white/30 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/50"
                autoFocus
              />
              {error && (
                <p className="text-red-300 text-sm">{error}</p>
              )}
              <Button 
                type="submit" 
                className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30 hover:border-white/50"
              >
                Access Site
              </Button>
            </form>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="absolute bottom-0 left-0 right-0 z-10 p-4">
          <div className="text-center text-sm text-white/40">
            © 2026 Freeman & Crown Equity Trust. All Rights Reserved.
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};